'use client';

import cx from 'classix';
import Image from 'next/image';
import {
  useLocale,
  useTranslations,
} from 'next-intl';
import { useState } from 'react';
import {
  Button as AriaButton,
  DialogTrigger,
} from 'react-aria-components';
import { BsStarFill } from 'react-icons/bs';
import { type DateTimeFormatOptions } from 'use-intl';

import { type Review } from '@/features/reviews/types/reviews-types';
import buttonStyles from '@/ui/button/button.module.scss';
import Modal from '@/ui/modal/modal';

import styles from './review-card.module.scss';

interface ReviewCardProps {
  review: Review;
  showFullDescription?: boolean;
  showDate?: boolean;
  view?: 'vertical' | 'horizontal';
}

export default function ReviewCard(props: ReviewCardProps) {
  const {
    review,
    showDate = false,
    showFullDescription = false,
    view = 'vertical',
  } = props;

  const translationsActions = useTranslations('global.actions');
  const reviewTranslations = useTranslations('review');
  const locale = useLocale();

  const options: DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  const formattedDate = new Intl.DateTimeFormat(locale, options).format(new Date(review.date));

  const fullStars = Array.from({ length: review.rating }, (_, index) => index);

  const [isReviewModalOpen,
    setIsReviewModalOpen] = useState(false);

  const handleModalOpen = () => {
    setIsReviewModalOpen(true);
  };

  return (
    <div className={cx(styles.reviewCard, styles[`reviewCard__${view}`])}>
      <div className={styles.reviewCard_top}>
        <div className={styles.reviewCard_avatar}>
          <Image
            src={`/images/user/${review.gender}-avatar.svg`}
            alt={review.name}
            width={62}
            height={62}
          />
        </div>

        <div className={styles.reviewCard_info}>
          <p>{review.name}</p>

          <p className={styles.reviewCard_userLocation}>
            {review.location}
          </p>

          <div className={styles.reviewCard_rating}>
            {fullStars.map((item) => (
              <div
                key={item}
                className={styles.reviewCard_star}
              >
                <BsStarFill />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.reviewCard_content}>
        <p className={styles.reviewCard_title}>
          {review.title}
        </p>

        <p
          className={cx(
            styles.reviewCard_description,
            showFullDescription && styles.reviewCard_description__full,
          )}
        >
          {review.description}
        </p>

        {showDate && (
          <p className={styles.reviewCard_date}>
            {reviewTranslations('review-left')}
            &nbsp;
            {formattedDate}
          </p>
        )}

        {!showFullDescription && (
          <DialogTrigger>
            <AriaButton
              className={cx(
                buttonStyles.base,
                buttonStyles.size_md,
                buttonStyles.variant_filled,
                buttonStyles.shade_dark,
              )}
              onPress={handleModalOpen}
            >
              {translationsActions('read')}
            </AriaButton>

            <Modal
              isOpen={isReviewModalOpen}
              onOpenChange={setIsReviewModalOpen}
              isCloseOnClickOutside
              showCloseIcon
            >
              <ReviewCard review={review} showFullDescription />
            </Modal>
          </DialogTrigger>
        )}
      </div>
    </div>
  );
}
